import { styled } from '@linaria/react';
import { Group, ScrollArea, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { BrandedLoadingOverlay } from '../../../../../components/loader/branded-loader';
import { TimelineTransition } from '../../../../../components/timeline/timeline-transition';
import { useAuditLogSidebar } from '../../../../../contexts/audit-log-sidebar.context';
import { formatDate } from '../../../../../helpers/date-time-helpers';
import { baseWhite, inkLight } from '../../../../../styles/design-tokens';
import { Divider } from '../../../../../ui-library/divider/divider';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { useWorkPeriodsAuditLogData } from '../../../hooks/work-periods-calculation.hooks';

export function AuditLogSidebar() {
  const { setIsAuditLogSidebarVisible } = useAuditLogSidebar();
  const { data: auditLogData, isFetching } = useWorkPeriodsAuditLogData();

  // Sort audit log entries by date (latest first)
  const sortedAuditLogEntries = useMemo(() => {
    if (!auditLogData?.audit_log) return [];

    return [...auditLogData.audit_log].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [auditLogData?.audit_log]);

  return (
    <SidebarContainer>
      <BrandedLoadingOverlay visible={isFetching} variant="colored" />

      <Group justify="space-between">
        <Text size="medium" weight="bold">{`Audit Log (${sortedAuditLogEntries.length})`}</Text>
        <Icon name="close" onClick={() => setIsAuditLogSidebarVisible(false)} />
      </Group>
      <Divider style={{ margin: '16px 0' }} />
      <ScrollArea style={{ flex: 1 }}>
        <div>
          {sortedAuditLogEntries.length > 0 ? (
            <TimelineContainer>
              <Timeline>
                {sortedAuditLogEntries.map((entry, index) => (
                  <AuditLogEntry key={entry.id} entry={entry} isLast={index === sortedAuditLogEntries.length - 1} />
                ))}
              </Timeline>
            </TimelineContainer>
          ) : (
            <EmptyState>
              <Icon name="history" size={48} color="dimmed" />
              <Text color="dimmed" size="large" weight="bold">
                No audit log entries
              </Text>
              <Text color="dimmed" size="small" style={{ textAlign: 'center' }}>
                No activity has been recorded for the selected work period.
              </Text>
            </EmptyState>
          )}
        </div>
      </ScrollArea>
    </SidebarContainer>
  );
}

const AuditLogEntry = ({ entry, isLast }: { entry: any; isLast: boolean }) => {
  return (
    <TimelineTransition isLast={isLast} iconDirection="up">
      <Text
        size="small"
        weight="medium"
        style={{
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          lineHeight: '1.4',
          overflowWrap: 'break-word',
          width: '100%',
          maxWidth: '100%',
          fontSize: '15px',
        }}
      >
        {entry.message || entry.identifier}
      </Text>
      <Text size="small" color={inkLight} style={{ fontSize: '13px' }}>
        {formatDate(entry.date, {
          utcInput: true,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          format: 'dddd MMM DD, YYYY [at] h:mm a z',
        })}
      </Text>
    </TimelineTransition>
  );
};

const SidebarContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${baseWhite};
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`;

const EmptyState = styled(Stack)`
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  gap: 16px;
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Timeline = styled.div`
  margin-top: 16px;
  padding-left: 8px;
`;
